/**
 * Credential storage utility for persisting user credentials in localStorage
 * This allows automatic re-login on page refresh
 */

import * as Sentry from '@sentry/react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@ais-ui/utils';

const CREDENTIALS_KEY = 'credentials';

export interface StoredCredentials {
  username: string;
  sessionId?: string;
  password?: string; // Only stored on localhost for easier re-authentication
  /** When the session was stored (epoch ms), to tell a stale session from a fresh one. */
  savedAt?: number;
  /** App release that stored the session, to spot release-boundary breakage. */
  release?: string;
}

/** Release the running app was built as, as configured on the Sentry client. */
export function getCurrentRelease(): string | undefined {
  try {
    return Sentry.getClient()?.getOptions().release;
  } catch {
    // Telemetry must never break storing or reading credentials.
    return undefined;
  }
}

export interface StoredSessionInfo {
  ageMs?: number;
  savedRelease?: string;
  currentRelease?: string;
  /** Undefined when either release is unknown. */
  releaseChanged?: boolean;
}

/** How old the stored session is and whether a release happened since. */
export function describeStoredSession(
  credentials: StoredCredentials | null | undefined,
  now = Date.now(),
): StoredSessionInfo {
  const currentRelease = getCurrentRelease();
  const savedRelease = credentials?.release;
  return {
    ageMs:
      credentials?.savedAt === undefined
        ? undefined
        : Math.max(0, now - credentials.savedAt),
    savedRelease,
    currentRelease,
    releaseChanged:
      savedRelease && currentRelease
        ? savedRelease !== currentRelease
        : undefined,
  };
}

/** Sentry tags/extras for a stored session; shared by logout and restore events. */
export function storedSessionTelemetry(info: StoredSessionInfo | undefined): {
  tags: Record<string, string>;
  extra: Record<string, unknown>;
} {
  const tags: Record<string, string> = {};
  const extra: Record<string, unknown> = {};
  if (!info) return { tags, extra };
  if (info.savedRelease) tags.session_saved_release = info.savedRelease;
  if (info.releaseChanged !== undefined) {
    tags.session_release_changed = String(info.releaseChanged);
  }
  if (info.ageMs !== undefined) extra.storedSessionAgeMs = info.ageMs;
  return { tags, extra };
}

/**
 * Save user credentials to localStorage
 * Password is optional and only saved on localhost for easier re-authentication
 */
export function saveCredentials(credentials: StoredCredentials): void {
  safeSetItem(
    CREDENTIALS_KEY,
    JSON.stringify({
      ...credentials,
      savedAt: Date.now(),
      release: getCurrentRelease(),
    }),
  );
}

/**
 * Get saved credentials from localStorage
 * Returns null if not found
 */
export function getSavedCredentials(): StoredCredentials | null {
  const credentialsStr = safeGetItem(CREDENTIALS_KEY);

  if (!credentialsStr) {
    return null;
  }

  try {
    return JSON.parse(credentialsStr);
  } catch (error) {
    console.error('Failed to parse saved credentials:', error);
    Sentry.captureException(error, {
      level: 'warning',
      tags: { credentials_storage_failure: 'parse' },
    });
    return null;
  }
}

/**
 * Clear credentials from localStorage
 * Call this on logout
 */
export function clearCredentials(): void {
  safeRemoveItem(CREDENTIALS_KEY);
}

/**
 * Check if credentials exist in localStorage
 */
export function hasStoredCredentials(): boolean {
  return getSavedCredentials() !== null;
}
